<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"> </slot>
      </template>
      <article slot="content">
        <section :class="{ 'truck-stage': true, isMobile }" ref="truckStage">
          <div class="truck-container" ref="truckContainer">
            <p class="provisional-letters-container" ref="provisionalLettersContainer">
              <span
                class="letter-to-drop"
                v-for="(char, index) in currentRound?.letterCombo"
                track-by="$index"
                :key="char + index"
              >
                {{ char }}
              </span>
            </p>
            <div class="truck-load" ref="truckLoad" v-if="currentRound?.letterCombo">
              <blinking-cursor-text
                class="type-line"
                :text="currentRound?.letterCombo"
                :textStep="this.progress.stepIndex"
              >
              </blinking-cursor-text>
              <img alt="Dump truck load" :src="truckLoadPath" />
            </div>
            <img alt="Dump truck" class="truck-body" :src="truckBodyPath" />
          </div>
        </section>
        <section
          :class="{ 'word-to-complete-stage': true, isMobile }"
          ref="wordToCompleteStage"
          v-if="currentRound?.steps && currentRound?.wordPart"
        >
          <div
            v-for="(step, index) in currentRound?.steps"
            :class="'word-container-' + index"
            :key="index"
          >
            <span
              v-for="(letter, charIndex) in step.answer"
              :class="getCharacterClass(charIndex, step.answer)"
              :key="charIndex + letter"
            >
              {{ letter }}
            </span>
          </div>
        </section>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TimelineLite, TweenMax, Bounce, Power4, Power2 } from 'gsap';

import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import { Affix } from 'graph/types/schema-generated-types';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import truckBodyPath from 'assets/images/activities/dump-the-combo/dump-combo-body.png';
import truckLoadPath from 'assets/images/activities/dump-the-combo/dump-truck-load.png';
import loadRaiseAudioPath from 'assets/audio/sfx/dump-truck-load-raise.mp3';

export default {
  mixins: [ActivityManager, TypingActivityHelper, ErrorLogger],

  components: {
    ActivityStage,
    BlinkingCursorText
  },

  data() {
    return {
      truckBodyPath,
      truckLoadPath,
      truckStage: null,
      truckContainer: null,
      provisionalLettersContainer: null,
      wordToCompleteStage: null,
      truckLoad: null
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    currentWordContainer() {
      return document.getElementsByClassName(`word-container-${this.progress.stepIndex}`)[0];
    },

    lettersToDrop() {
      return document.getElementsByClassName('letter-to-drop');
    }
  },
  methods: {
    async loadActivity() {
      this.createRounds(this.getActivityState());
      await this.loadSounds();
    },

    async loadSounds() {
      this.sfx = await this.getActivityRoundSounds(
        this.rounds,
        this.spokenWordsPrefix,
        'ft',
        'answer',
        [loadRaiseAudioPath]
      );
    },

    getKeyboardConfig() {
      const {
        keyboard: { keyboardType, roofs }
      } = this.activityData;

      return {
        showKeyboardAtStart: false,
        keyboardType,
        roofs
      };
    },

    getActivityState() {
      const {
        rounds,
        keyboard: { characters }
      } = this.activityData;
      const isTouchAndHold = typeof characters === 'string';

      return {
        rounds,
        instructions: {
          written: `${isTouchAndHold ? 'Hold the target. ' : ''}Type each letter combo.`,
          voice: `${this.voiceInstructionsPrefix}/dumpthecombo${
            isTouchAndHold ? '' : 'nth'
          }-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentRound']['letterCombo'];
    },

    async startActivity() {
      await this.advance({ newRound: true });
      this.showKeyboard();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);
    },

    advance({ newRound }) {
      if (newRound) {
        this.setupNewRound({ stepCallback: this.stepCallback });
      }

      return new Promise(resolve =>
        this.$nextTick(async () => {
          await this.returnTruck();
          this.setWordContainerVisibility();
          this.returnLetters();
          this.toggleDroppedLettersVisibility(true);
          this.enableKeyboard();
          resolve();
        })
      );
    },

    async handleStepEnd() {
      const { left, right } = this.currentWordContainer.getBoundingClientRect();
      const dropX =
        this.currentRound?.wordPart === Affix.SUFFIX
          ? right - this.$refs.provisionalLettersContainer.getBoundingClientRect().width
          : left;

      this.disableKeyboard();
      await this.startTruck(dropX, 120, 1);

      if (this.isEndOfActivity) {
        this.completeActivity();
      } else {
        this.completeStep();
        if (this.isEndOfRound) {
          this.resetCompletedSteps();
          await this.hideWordContainers();
          this.resetWordContainersStyling();
        } else {
          await this.hideUnderlines();
          await this.moveWordContainer(50);
          this.setCurrentStep({ stepCallback: this.stepCallback });
        }
        this.advance({ newRound: this.isEndOfRound });
      }
    },

    getTruckRelativeX(absoluteX) {
      return absoluteX - this.$refs.truckContainer.getBoundingClientRect().left;
    },

    async handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence, {
        rerenderAlert: true
      });

      if (this.isEndOfCurrentWord) {
        await this.handleStepEnd();
      }
    },

    getCharacterClass(charIndex, currentWord) {
      const { wordPart, letterCombo } = this.currentRound;
      let className = '';

      if (!letterCombo || !currentWord) return className;

      if (wordPart === Affix.SUFFIX) {
        if (charIndex >= currentWord.length - letterCombo.length) {
          className = ' missing';
        }
      } else if (wordPart === Affix.PREFIX) {
        if (charIndex < letterCombo.length) {
          className = 'missing';
        }
      }

      return className;
    },

    returnLetters() {
      TweenMax.set(this.lettersToDrop, { x: 0, y: 0, rotation: 0 });
      TweenMax.set(this.$refs.provisionalLettersContainer, { x: 0 });
    },

    dropLetters(offsetX) {
      return new Promise(resolve => {
        TweenMax.staggerTo(
          this.lettersToDrop,
          0.7,
          {
            x: -offsetX,
            y: this.isMobile ? 231 : this.isChromeOS ? 226 : 276,
            rotation: -360,
            ease: Bounce.easeOut
          },
          0.2,
          resolve
        );
      });
    },

    switchLetterVisibility() {
      const timeline = new TimelineLite();
      const missingLetters = this.currentWordContainer.getElementsByClassName('missing');

      timeline.call(this.toggleDroppedLettersVisibility).set(missingLetters, { color: 'black' });
    },

    toggleDroppedLettersVisibility(show) {
      TweenMax.set(this.$refs.provisionalLettersContainer, {
        opacity: show ? 1 : 0
      });
    },

    async startTruck(absoluteDestinationX, spotOffset = 0, secondsToStayOnSpot = 0) {
      const relativeDestinationX = this.getTruckRelativeX(absoluteDestinationX) + spotOffset;
      const letterContainerXOffset = -50;
      const xOffsetCompensation = this.currentRound?.wordPart === Affix.PREFIX ? 8 : 6;

      await this.moveTruckToSpot(relativeDestinationX);
      this.sfx[loadRaiseAudioPath].play();
      await this.raiseTruckLoad(letterContainerXOffset);
      await this.dropLetters(spotOffset + letterContainerXOffset + xOffsetCompensation);
      this.switchLetterVisibility();

      return new Promise(resolve => {
        this.playStepSound(this.sfx);
        TweenMax.delayedCall(secondsToStayOnSpot, async () => {
          this.lowerTruckLoad();
          await this.moveTruckFromSpot(relativeDestinationX);
          resolve();
        });
      });
    },

    returnTruck() {
      const { width: truckContainerWidth } = this.$refs.truckStage.getBoundingClientRect();

      return new Promise(resolve => {
        TweenMax.fromTo(
          this.$refs.truckContainer,
          1.1,
          {
            // Add the CSS-set margin to the offset
            x: -truckContainerWidth - 30
          },
          {
            x: 0,
            autoAlpha: 1,
            ease: Power2.easeOut,
            onComplete: resolve
          }
        );
      });
    },

    lowerTruckLoad() {
      TweenMax.to(this.$refs.truckLoad, 0.5, {
        rotation: 0,
        x: 0,
        transformOrigin: 'left bottom'
      });
    },

    raiseTruckLoad(letterContainerXOffset) {
      const timeline = new TimelineLite();

      return new Promise(resolve => {
        timeline
          .to(this.$refs.truckLoad, 1.2, {
            rotation: -25,
            x: 0,
            transformOrigin: 'left bottom',
            ease: Power4.easeOut,
            onComplete: resolve
          })
          .to(this.$refs.provisionalLettersContainer, 1.0, {
            x: letterContainerXOffset
          });
      });
    },

    moveTruckFromSpot(relativeStartX) {
      const { right: truckStageAbsoluteRight } = this.$refs.truckStage.getBoundingClientRect();
      const relativeDestinationX = this.getTruckRelativeX(truckStageAbsoluteRight);

      return new Promise(resolve => {
        TweenMax.to(this.$refs.truckContainer, 0.5, {
          x: relativeStartX + relativeDestinationX,
          ease: Power2.easeIn,
          onComplete: resolve
        });
      });
    },

    moveTruckToSpot(relativeDestinationX) {
      return new Promise(resolve => {
        TweenMax.to(this.$refs.truckContainer, 1.1, {
          x: relativeDestinationX,
          ease: Power2.easeOut,
          onComplete: resolve
        });
      });
    },

    hideWordContainers() {
      const wordContainers = this.$refs.wordToCompleteStage.childNodes;

      return new Promise(resolve => {
        TweenMax.staggerTo(
          wordContainers,
          0.25,
          {
            opacity: 0
          },
          Math.random() / 10,
          resolve
        );
      });
    },

    resetWordContainersStyling() {
      TweenMax.set(this.$refs.wordToCompleteStage.getElementsByTagName('div'), {
        clearProps: 'all'
      });
      TweenMax.set(this.$refs.wordToCompleteStage.getElementsByTagName('span'), {
        clearProps: 'all'
      });
    },

    hideUnderlines() {
      const underlinedLetters = this.currentWordContainer.getElementsByClassName('missing');

      return new Promise(resolve => {
        TweenMax.to(underlinedLetters, 0.25, {
          borderBottom: 'none',
          onComplete: resolve
        });
      });
    },

    moveWordContainer(marginToLeave) {
      const { stepIndex } = this.progress;
      const { width: wordContainerWidth } = this.currentWordContainer.getBoundingClientRect();
      const contentPanelEl = document.querySelector('.contentPanel');

      const isFirstStep = stepIndex === 0;
      const originalContainerWidth =
        contentPanelEl && contentPanelEl.offsetWidth > 1023 ? contentPanelEl.offsetWidth : 1023;

      return new Promise(resolve => {
        TweenMax.to(this.currentWordContainer, 0.5, {
          left: isFirstStep
            ? 0 + marginToLeave
            : originalContainerWidth - wordContainerWidth - marginToLeave,
          transform: 'translateY(-50%) scale(0.7)',
          onComplete: resolve
        });
      });
    },

    getElementTransformCoordinates(element) {
      const transformValues = window.getComputedStyle(element).transform.split(',');

      return {
        x: parseInt(transformValues[4], 10),
        y: parseInt(transformValues[5], 10)
      };
    },

    setWordContainerVisibility() {
      TweenMax.to(this.currentWordContainer, 0.5, { opacity: 1 });
    }
  },
  watch: {
    currentStep(newVal) {
      if (newVal && newVal.answer) {
        let instructions = `Type the letter combo for the dump truck to drop the letter and form a word - ${this.currentRound.letterCombo}`;

        this.rerenderScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-stage {
  p {
    margin: 0;
  }

  .truck-stage {
    // We need to set a height in order to be able to mimic the road with
    // css: border-bottom, because its children are absolutely positioned
    height: 205px;
    border-bottom: 5px solid #aeaeae;
    width: 1024px;
    overflow-x: clip;

    &.isMobile {
      width: 100%;
    }

    .truck-container {
      position: relative;
      left: 30px;
      height: 210px;
      width: 280px;
      opacity: 0;

      .truck-load,
      .truck-body {
        position: absolute;
      }

      .provisional-letters-container {
        font-family: hwtfutura;
        position: absolute;
        top: 0;
        left: 3%;
        font-size: 60px;
        z-index: 1;

        span {
          color: black;
          display: inline-block;
          margin-right: 2px;
        }
      }

      .truck-load {
        top: 0;
        left: 0;
        z-index: 2;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;

        .type-line {
          position: absolute;
          display: block;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 60px;
          letter-spacing: 0px;
          z-index: 3;
        }
      }

      .truck-body {
        top: 55px;
        left: 30px;
        z-index: 3;
      }
    }

    &.isMobile {
      width: 100%;
    }
  }

  .word-to-complete-stage {
    position: relative;
    height: 225px;
    width: 1024px;

    [class*='word-container-'] {
      opacity: 0;
      font-family: hwtfutura;
      font-size: 60px;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      span {
        margin-right: 2px;
        display: inline-block;

        &:last-child {
          margin-right: 0;
        }

        &.missing {
          color: transparent;
          border-bottom: 4px solid black;
          height: 65px;
        }
      }
    }

    &.isMobile {
      height: 141px !important;
      width: 100%;
    }
  }
}

.chrome-os {
  .word-to-complete-stage {
    height: 125px;
  }
}
</style>
